// src/context/CartContext.jsx - Updated to work with InventoryItems
import React, { createContext, useState, useEffect } from 'react';
import apiService from '../services/api';
import { useAuth } from './AuthContext';

// Create the context
export const CartContext = createContext();

// Create the provider component
export const CartProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [cart, setCart] = useState([]);
  const [groupedCart, setGroupedCart] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openOrder, setOpenOrder] = useState(null);
  const [openOrders, setOpenOrders] = useState([]);
  const [pixieDustCartItem, setPixieDustCartItem] = useState(null);

  // Load cart when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      getCart();
      fetchOpenOrder();
    } else {
      // For non-authenticated users, we can't use inventory items
      // so we maintain the old product-based cart in localStorage
      loadLocalCart();
      setOpenOrder(null);
      setOpenOrders([]);
    }
  }, [isAuthenticated]);

  // Load local cart for non-authenticated users
  const loadLocalCart = () => {
    try {
      const localCart = localStorage.getItem('disneyShopperCart');
      if (localCart) {
        const parsedCart = JSON.parse(localCart);
        setCart(parsedCart);
        groupCartByProduct(parsedCart);
      }
    } catch (error) {
      console.error('Error loading local cart:', error);
      localStorage.removeItem('disneyShopperCart');
    }
  };

  // Save to localStorage for non-authenticated users
  const saveLocalCart = (cartData) => {
    try {
      localStorage.setItem('disneyShopperCart', JSON.stringify(cartData));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  // Group cart items by product for easier display
  const groupCartByProduct = (cartItems) => {
    if (!isAuthenticated) {
      // For local cart, items are already products
      setGroupedCart(cartItems);
      return;
    }

    const grouped = cartItems.reduce((acc, cartItem) => {
      const isPreorder = cartItem.isPreorder;
      let productData, productId;

      if (isPreorder) {
        productData = cartItem.product;
        productId = cartItem.product._id;
      } else {
        productData = cartItem.inventoryItem.product;
        productId = cartItem.inventoryItem.product._id;
      }

      const existingGroup = acc.find(group => group.product._id === productId);

      const inventoryItemData = {
        cartItemId: cartItem._id,
        inventoryItemId: isPreorder ? `preorder-${cartItem._id}` : cartItem.inventoryItem._id,
        itemCode: isPreorder ? `PO-${cartItem._id}` : cartItem.inventoryItem.itemCode,
        size: isPreorder ? cartItem.preorderSize : cartItem.inventoryItem.itemDetails?.size,
        color: isPreorder ? null : cartItem.inventoryItem.itemDetails?.color,
        condition: isPreorder ? 'new' : cartItem.inventoryItem.condition,
        addedAt: cartItem.addedAt,
        isPreorder: isPreorder
      };

      if (existingGroup) {
        existingGroup.inventoryItems.push(inventoryItemData);
      } else {
        acc.push({
          product: productData,
          inventoryItems: [inventoryItemData],
          quantity: 1
        });
      }

      return acc;
    }, []);

    // Update quantities
    grouped.forEach(group => {
      group.quantity = group.inventoryItems.length;
    });

    setGroupedCart(grouped);
  };

  // Get cart from server
  const getCart = async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiService.cart.getCart();
      if (response.data.success) {
        setCart(response.data.data.items);
        groupCartByProduct(response.data.data.items);
        setPixieDustCartItem(response.data.data.pixieDustItem || null);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      setError('Failed to load cart');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch the user's open orders (now returns array for multi-posting-date support)
  const fetchOpenOrder = async () => {
    if (!isAuthenticated) return;
    try {
      const response = await apiService.orders.getOpenOrder();
      if (response.data.success) {
        const data = response.data.data;
        // Backend now returns an array of open orders
        const ordersArray = Array.isArray(data) ? data : (data ? [data] : []);
        setOpenOrders(ordersArray);
        // Backward compat: openOrder = first standard order, or first order
        const stdOrder = ordersArray.find(o => !o.postingDateSlot || o.postingDateSlot === 'standard');
        setOpenOrder(stdOrder || ordersArray[0] || null);
      }
    } catch (error) {
      console.error('Error fetching open order:', error);
      setOpenOrder(null);
      setOpenOrders([]);
    }
  };

  // Add inventory item to cart (authenticated users)
  const addInventoryItemToCart = async (inventoryItemId) => {
    if (!isAuthenticated) {
      throw new Error('Must be logged in to add inventory items');
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('Adding to cart:', inventoryItemId);
      const response = await apiService.cart.addInventoryItem(inventoryItemId);

      if (response.data.success) {
        // Reload cart to get updated data
        await getCart();
        return response.data.data;
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      const errorMessage = error.response?.data?.message || 'Failed to add item to cart';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Add inventory item to cart by finding an available one with specific variants
  const addToCartByVariant = async (productId, variantSelection = {}, isPreorder = false) => {
    if (!isAuthenticated) {
      throw new Error('Must be logged in to add inventory items');
    }

    setIsLoading(true);
    setError(null);

    try {
      // First, get available inventory items for this product with these variants
      const filters = {
        ...variantSelection
      };
      console.log('Finding available inventory items for product:', productId, 'with filters:', filters);
      
      const availableItems = await getAvailableInventoryItems(productId, filters);
      
      if (!availableItems || availableItems.length === 0) {
        throw new Error('No available inventory items match your selection');
      }
      
      // Find the first available item that's not already in cart
      const cartInventoryIds = cart.map(item => item.inventoryItem?._id).filter(Boolean);
      const availableItem = availableItems.find(item => !cartInventoryIds.includes(item._id));
      
      if (!availableItem) {
        throw new Error('Selected item is no longer available');
      }
      
      // Add this specific inventory item to cart
      const response = await apiService.cart.addInventoryItem(availableItem._id);
      
      if (response.data.success) {
        // Reload cart to get updated data
        await getCart();
        return response.data.data;
      }
    } catch (error) {
      console.error('Error adding to cart by variant:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add item to cart';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Remove from cart
  const removeFromCart = async (itemId) => {
    if (isAuthenticated) {
      // Remove cart item by ID
      try {
        setIsLoading(true);
        await apiService.cart.removeItem(itemId);
        await getCart(); // Reload cart
      } catch (error) {
        console.error('Error removing from cart:', error);
        setError('Failed to remove item from cart');
      } finally {
        setIsLoading(false);
      }
    } else {
      // Remove from local cart
      const newCart = cart.filter(item => item.id !== itemId);
      setCart(newCart);
      groupCartByProduct(newCart);
      saveLocalCart(newCart);
    }
  };

  // Update quantity (only for local cart)
  const updateQuantity = (productId, newQuantity) => {
    if (isAuthenticated) {
      throw new Error('Cannot update quantity for inventory items');
    }

    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    const newCart = cart.map(item => 
      item.id === productId ? { ...item, quantity: newQuantity } : item
    );
    
    setCart(newCart);
    groupCartByProduct(newCart);
    saveLocalCart(newCart);
  };

  // Clear cart
  const clearCart = async () => {
    if (isAuthenticated) {
      try {
        setIsLoading(true);
        await apiService.cart.clearCart();
        setCart([]);
        setGroupedCart([]);
      } catch (error) {
        console.error('Error clearing cart:', error);
        setError('Failed to clear cart');
      } finally {
        setIsLoading(false);
      }
    } else {
      setCart([]);
      setGroupedCart([]);
      localStorage.removeItem('disneyShopperCart');
    }
  };

  // Get available inventory items for a product
  const getAvailableInventoryItems = async (productId, filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      console.log('Getting available inventory items for product:', productId, 'with queryParams:', queryParams);
      const response = await apiService.inventory.getAvailableItems(productId, queryParams);
      
      if (response.data.success) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching available inventory:', error);
      return [];
    }
  };

  const getAvailableQuantity = async (productId) => {
    try {
      response = await apiService.products.getStock(productId);
      if (response.data.success) {
        return response.data.data.availableQuantity;
      }
      return 0;
    } catch (error) {
      console.error('Error fetching available stock:', error);
      return 0;
    }
  };
    

  // Add a request item to cart (calls the request-specific endpoint, then refreshes cart)
  const addRequestItemToCart = async (requestId, itemId) => {
    if (!isAuthenticated) {
      throw new Error('Must be logged in to add request items to cart');
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.requests.addItemToCart(requestId, itemId);
      if (response.data.success) {
        await getCart();
        return response.data.data;
      }
    } catch (error) {
      console.error('Error adding request item to cart:', error);
      const errorMessage = error.response?.data?.message || 'Failed to add request item to cart';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    if (isAuthenticated) {
      // For authenticated users, each inventory item has its own price
      return groupedCart.reduce((total, group) => {
        return total + (group.product.price * group.inventoryItems.length);
      }, 0);
    } else {
      // For local cart, use product price * quantity
      return cart.reduce((total, item) => {
        return total + (item.price * item.quantity);
      }, 0);
    }
  };

  // Get cart item count
  const getCartItemCount = () => {
    if (isAuthenticated) {
      return cart.length; // Each inventory item counts as 1
    } else {
      return cart.reduce((count, item) => count + item.quantity, 0);
    }
  };

  // Get cart items separated by type (regular vs preorder)
  const getCartItemsByType = () => {
    const regularItems = [];
    const preorderItems = [];

    groupedCart.forEach(group => {
      // Check if any inventory item in the group is a preorder
      const hasPreorder = group.inventoryItems.some(item => item.isPreorder);
      if (hasPreorder) {
        preorderItems.push(group);
      } else {
        regularItems.push(group);
      }
    });

    return { regularItems, preorderItems };
  };

  // Create value object with all cart functionality
  const value = {
    // State
    cart,
    groupedCart,
    isLoading,
    error,
    openOrder,
    openOrders,
    pixieDustCartItem,

    // Actions
    addInventoryItemToCart,
    addToCartByVariant,
    addRequestItemToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCart,
    fetchOpenOrder,
    getAvailableInventoryItems,
    
    // Computed values
    totalAmount: calculateTotals(),
    cartItemCount: getCartItemCount(),
    getCartItemsByType,
    
    // Backward compatibility
    addToCart: addInventoryItemToCart,
    regularTotal: (() => {
      const { regularItems } = getCartItemsByType();
      return regularItems.reduce((total, group) => {
        return total + (group.product.price * group.inventoryItems.length);
      }, 0);
    })(),
    preorderTotal: (() => {
      const { preorderItems } = getCartItemsByType();
      return preorderItems.reduce((total, group) => {
        return total + (group.product.price * group.inventoryItems.length);
      }, 0);
    })(),
    hasPreorderItems: groupedCart.some(group => group.inventoryItems.some(item => item.isPreorder)),
  };
  
  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};